// src/pages/KnowledgeDBPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Database,
  Plus,
  Search,
  Filter,
  ChevronDown,
  FileText,
  Share2,
  CheckCircle,
  HelpCircle,
  TrendingUp,
  Activity,
  Zap,
  Brain,
} from "lucide-react";
import {
  Tooltip,
  CreateDatabaseModal,
  ShareDatabaseModal,
  DeleteConfirmModal,
  DatabaseCard,
  EmptyState,
  LoadingSkeleton,
} from "../components/KnowledgeDB";
import useKnowledgeDB from "../hooks/useKnowledgeDB";
import useIAM from "../hooks/useIam";
import { toast } from "../modal/DBToast";
import MainHeader from "../MainHeader";
const KnowledgeDatabasePage = () => {
  // State management
  const [databases, setDatabases] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("created_desc");

  // Hooks
  const {
    getDatabases,
    createDatabase,
    shareDatabase,
    deleteDatabase,
    fileToBase64,
    validatePDFFile,
    formatFileSize,
    formatDate,
    calculateStats,
  } = useKnowledgeDB();

  const { fetchIAMList } = useIAM();

  // Load initial data
  useEffect(() => {
    loadDatabases();
    loadUsers();
  }, []);

  // Auto refresh for progressing databases
  useEffect(() => {
    const hasProgressing = databases.some(
      (db) =>
        db.database_status === "analyzing" ||
        db.database_status === "progressing"
    );

    if (hasProgressing) {
      const interval = setInterval(loadDatabases, 300000); // 5초마다 갱신
      return () => clearInterval(interval);
    }
  }, [databases]);

  const loadDatabases = useCallback(async () => {
    setIsLoading(true);
    const result = await getDatabases();

    if (result.ok) {
      setDatabases(result.databases);
    } else {
      toast.error(
        "오류",
        result.error || "데이터베이스 목록을 불러올 수 없습니다."
      );
    }

    setIsLoading(false);
  }, [getDatabases]);

  const loadUsers = useCallback(async () => {
    const result = await fetchIAMList();
    if (result.ok) {
      setUsers(result.members.filter((m) => !m.is_me));
    }
  }, [fetchIAMList]);

  // Handle create database
  const handleCreate = async (formData) => {
    setActionLoading(true);

    try {
      // Validate file
      const validation = validatePDFFile(formData.file);
      if (!validation.valid) {
        toast.error("파일 오류", validation.error);
        setActionLoading(false);
        return;
      }

      // Convert file to base64
      const base64 = await fileToBase64(formData.file);

      // API call with toast.promise
      const result = await toast.promise(
        createDatabase({
          database_name: formData.database_name,
          database_description: formData.database_description,
          file_name: formData.file.name,
          file_type: "pdf",
          b64: base64,
        }),
        {
          loading: "데이터베이스 생성 중...",
          success: "데이터베이스 생성이 시작되었습니다!",
          error: "데이터베이스 생성에 실패했습니다",
        }
      );

      if (result.ok) {
        setCreateModalOpen(false);
        await loadDatabases();
        toast.info(
          "안내",
          "백그라운드에서 PDF 분석이 진행됩니다. 잠시 후 목록에서 확인하실 수 있습니다."
        );
      } else if (result.invalidFormat) {
        toast.error("포맷 오류", "PDF 파일만 업로드 가능합니다.");
      } else {
        toast.error(
          "생성 실패",
          result.error || "데이터베이스를 생성할 수 없습니다."
        );
      }
    } catch (error) {
      console.error("Create database error:", error);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle share database
  const handleShare = async (databaseId, userIds) => {
    setActionLoading(true);

    const result = await shareDatabase(databaseId, userIds);

    if (result.ok) {
      toast.success(
        "공유 완료",
        `${userIds.length}명의 사용자에게 데이터베이스가 공유되었습니다.`
      );
      setShareModalOpen(false);
    } else if (result.unknown) {
      toast.error("공유 실패", "데이터베이스를 찾을 수 없습니다.");
    } else if (result.unable) {
      toast.warning(
        "공유 불가",
        "완료된 직접 구축 데이터베이스만 공유 가능합니다."
      );
    } else {
      toast.error(
        "공유 실패",
        result.error || "데이터베이스를 공유할 수 없습니다."
      );
    }

    setActionLoading(false);
  };

  // Handle delete database
  const handleDelete = async (databaseId) => {
    setActionLoading(true);

    const result = await deleteDatabase(databaseId);

    if (result.ok) {
      toast.success("삭제 완료", "데이터베이스가 삭제되었습니다.");
      setDeleteModalOpen(false);
      await loadDatabases();
    } else if (result.unknown) {
      toast.error("삭제 실패", "데이터베이스를 찾을 수 없습니다.");
    } else {
      toast.error(
        "삭제 실패",
        result.error || "데이터베이스를 삭제할 수 없습니다."
      );
    }

    setActionLoading(false);
  };

  // Filter and sort databases
  const getFilteredDatabases = useCallback(() => {
    let filtered = [...databases];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (db) =>
          db.database_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          db.database_description
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (filterType !== "all") {
      filtered = filtered.filter((db) => {
        if (filterType === "direct") return db.base_type === "build_typ_d001";
        if (filterType === "shared") return db.base_type === "build_typ_s001";
        return true;
      });
    }

    // Status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((db) => db.database_status === filterStatus);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return a.database_name.localeCompare(b.database_name);
        case "name_desc":
          return b.database_name.localeCompare(a.database_name);
        case "size_asc":
          return (a.data_size || 0) - (b.data_size || 0);
        case "size_desc":
          return (b.data_size || 0) - (a.data_size || 0);
        case "created_asc":
          return new Date(a.created_at) - new Date(b.created_at);
        case "created_desc":
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return filtered;
  }, [databases, searchTerm, filterType, filterStatus, sortBy]);

  const filteredDatabases = getFilteredDatabases();
  const stats = calculateStats(databases);

  return (
    <>
      <MainHeader />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                    <Database className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-bold text-gray-900">
                        지식 데이터베이스
                      </h1>
                      {/* <Tooltip
                      content="PDF를 AI가 인지할 수 있는 수치로 변환하여 스마트 에디터와 연결하여 문서를 작성하기 위한 배경지식을 제공합니다"
                      position="bottom"
                    >
                      <HelpCircle className="w-5 h-5 text-gray-400 cursor-help" />
                    </Tooltip> */}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      AI 기반 문서 분석 및 지식 관리 시스템
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <Plus className="w-5 h-5" />새 데이터베이스
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 mb-1">
                  지식 데이터베이스란?
                </h3>
                <p className="text-xs text-gray-700 leading-relaxed">
                  PDF 문서를 AI가 이해할 수 있는 벡터 형태로 변환하여 저장하는
                  시스템입니다. 변환된 데이터는 스마트 에디터에서 문서 작성 시
                  참고 자료로 활용되어 더 정확하고 풍부한 컨텐츠 생성을
                  지원합니다.
                </p>
                <div className="flex gap-4 mt-3">
                  <div className="flex items-center gap-1">
                    <Zap className="w-3 h-3 text-yellow-600" />
                    <span className="text-xs text-gray-600">
                      실시간 벡터 변환
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Activity className="w-3 h-3 text-green-600" />
                    <span className="text-xs text-gray-600">
                      스마트 에디터 연동
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Share2 className="w-3 h-3 text-purple-600" />
                    <span className="text-xs text-gray-600">팀 공유 지원</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                    전체
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.total}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">데이터베이스</p>
                </div>
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Database className="w-6 h-6 text-gray-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                    직접 구축
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {stats.direct}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">원본 DB</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                    공유받음
                  </p>
                  <p className="text-2xl font-bold text-purple-600">
                    {stats.shared}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">공유 DB</p>
                </div>
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Share2 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 font-medium uppercase tracking-wider">
                    완료
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.complete}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">사용 가능</p>
                </div>
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Additional Stats */}
          {stats.total > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
              <div className="bg-white/60 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-gray-600">진행중</span>
                <span className="text-sm font-semibold text-yellow-600">
                  {stats.progressing}
                </span>
              </div>
              <div className="bg-white/60 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-gray-600">실패</span>
                <span className="text-sm font-semibold text-red-600">
                  {stats.failed}
                </span>
              </div>
              <div className="bg-white/60 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-gray-600">총 용량</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatFileSize(stats.totalSize)}
                </span>
              </div>
              <div className="bg-white/60 rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-gray-600">총 청크</span>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.totalChunks.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="데이터베이스 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <div className="relative">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white cursor-pointer"
                  >
                    <option value="all">모든 유형</option>
                    <option value="direct">직접 구축</option>
                    <option value="shared">공유받음</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white cursor-pointer"
                  >
                    <option value="all">모든 상태</option>
                    <option value="analyzing">분석중</option>
                    <option value="progressing">빌드중</option>
                    <option value="complete">완료</option>
                    <option value="faild">실패</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white cursor-pointer"
                  >
                    <option value="created_desc">최신순</option>
                    <option value="created_asc">오래된순</option>
                    <option value="name_asc">이름순 (A-Z)</option>
                    <option value="name_desc">이름순 (Z-A)</option>
                    <option value="size_desc">크기 큰순</option>
                    <option value="size_asc">크기 작은순</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Active filters display */}
            {(searchTerm || filterType !== "all" || filterStatus !== "all") && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                <span className="text-xs text-gray-500">필터:</span>
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-xs">
                    검색: {searchTerm}
                    <button
                      onClick={() => setSearchTerm("")}
                      className="ml-1 hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filterType !== "all" && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-xs">
                    유형: {filterType === "direct" ? "직접 구축" : "공유받음"}
                    <button
                      onClick={() => setFilterType("all")}
                      className="ml-1 hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filterStatus !== "all" && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-md text-xs">
                    상태: {filterStatus}
                    <button
                      onClick={() => setFilterStatus("all")}
                      className="ml-1 hover:text-red-600"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterType("all");
                    setFilterStatus("all");
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 ml-2"
                >
                  모두 초기화
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Database Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          {isLoading ? (
            <LoadingSkeleton />
          ) : filteredDatabases.length === 0 ? (
            <EmptyState
              searchTerm={searchTerm}
              filterType={filterType}
              filterStatus={filterStatus}
              onCreateClick={() => setCreateModalOpen(true)}
            />
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  총{" "}
                  <span className="font-semibold">
                    {filteredDatabases.length}
                  </span>
                  개의 데이터베이스
                </p>
                {stats.progressing > 0 && (
                  <p className="text-xs text-yellow-600 flex items-center gap-1">
                    <Activity className="w-3 h-3 animate-pulse" />
                    {stats.progressing}개 처리중 (자동 새로고침)
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredDatabases.map((database) => (
                  <DatabaseCard
                    key={database.database_id}
                    database={database}
                    onShare={(db) => {
                      setSelectedDatabase(db);
                      setShareModalOpen(true);
                    }}
                    onDelete={(db) => {
                      setSelectedDatabase(db);
                      setDeleteModalOpen(true);
                    }}
                    formatFileSize={formatFileSize}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Modals */}
        <CreateDatabaseModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreate}
          isLoading={actionLoading}
        />

        <ShareDatabaseModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          onShare={handleShare}
          database={selectedDatabase}
          users={users}
          isLoading={actionLoading}
        />

        <DeleteConfirmModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDelete}
          database={selectedDatabase}
          isLoading={actionLoading}
        />
      </div>
    </>
  );
};

export default KnowledgeDatabasePage;
